import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, X, Calendar, Gem, Image as ImageIcon } from "lucide-react";

const typeMeta = {
  subscription: { icon: Gem, label: "Subscription", color: "#FF1493" },
  unlock: { icon: ImageIcon, label: "Content unlock", color: "#FFB5D3" },
  booking: { icon: Calendar, label: "Booking deposit", color: "#E5B299" },
};

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState({});
  const [filter, setFilter] = useState("pending");

  const load = () => http.get("/admin/payments").then((r) => setPayments(r.data));
  useEffect(() => { load(); }, []);

  const markReceived = async (id) => {
    if (!confirm("Confirm you've received this e-Transfer? This will grant access / activate membership / unlock content.")) return;
    setLoading((p) => ({ ...p, [id]: true }));
    try {
      await http.post(`/admin/payments/${id}/mark-received`);
      toast.success("Payment confirmed.");
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading((p) => ({ ...p, [id]: false })); }
  };

  const cancelTx = async (id) => {
    if (!confirm("Cancel this payment request?")) return;
    try {
      await http.post(`/admin/payments/${id}/cancel`);
      toast.success("Cancelled.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const filtered = payments.filter((p) => {
    if (filter === "pending") return p.payment_status !== "paid" && p.status !== "cancelled";
    if (filter === "paid") return p.payment_status === "paid";
    if (filter === "cancelled") return p.status === "cancelled";
    return true;
  });

  return (
    <div data-testid="admin-payments-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-2">Revenue</div>
        <h1 className="font-display text-4xl text-champagne">E-Transfer <span className="italic text-pink-hot">payments</span></h1>
        <p className="text-sm text-champagne/60 mt-3 max-w-2xl">
          Every subscription, content unlock, and booking deposit appears here as soon as a client clicks through. When the
          e-Transfer arrives in your inbox, click <strong className="text-pink-hot">Mark as Received</strong> and the
          client's access is activated and they get an email confirmation automatically.
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        {[
          { id: "pending", label: "Pending" },
          { id: "paid", label: "Received" },
          { id: "cancelled", label: "Cancelled" },
          { id: "all", label: "All" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            data-testid={`filter-${t.id}`}
            className={`px-4 py-2 text-[11px] tracking-[0.28em] uppercase rounded-sm transition-all ${
              filter === t.id
                ? "bg-pink-hot text-white glow-pink"
                : "border border-rosegold/20 text-champagne/70 hover:border-pink-hot/40"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-champagne/60 glass-panel p-8 rounded-md">No payments in this view.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => {
            const meta = typeMeta[p.type] || { icon: Gem, label: p.type, color: "#FFB5D3" };
            const Icon = meta.icon;
            const isPaid = p.payment_status === "paid";
            const isCancelled = p.status === "cancelled";
            return (
              <div key={p.id} className={`glass-panel rounded-md p-5 flex items-center gap-4 flex-wrap ${isCancelled ? "opacity-50" : ""}`} data-testid={`payment-${p.id}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40` }}>
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-rosegold">{meta.label}</div>
                  <div className="font-display italic text-xl text-champagne truncate">{p.label || p.type}</div>
                  <div className="text-xs text-champagne/70 mt-1 truncate">{p.email || "anonymous"}</div>
                  <div className="text-[10px] text-champagne/40 mt-1">Ref {p.id?.slice(0, 8).toUpperCase()} · {new Date(p.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right min-w-[110px]">
                  <span className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-sm ${
                    isCancelled ? "bg-champagne/10 text-champagne/60" :
                    isPaid ? "bg-pink-hot/20 text-pink-hot" : "bg-rosegold/20 text-rosegold"
                  }`}>
                    {isCancelled ? "Cancelled" : isPaid ? "Received" : "Awaiting"}
                  </span>
                  <div className="font-display text-2xl text-pink-hot mt-2">${Number(p.amount).toFixed(0)}</div>
                </div>
                <div className="flex flex-col gap-2 min-w-[180px]">
                  {!isPaid && !isCancelled && (
                    <button
                      onClick={() => markReceived(p.id)}
                      disabled={loading[p.id]}
                      className="px-4 py-2 bg-pink-hot text-white text-[10px] tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all disabled:opacity-60 flex items-center gap-2 justify-center"
                      data-testid={`mark-received-${p.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      {loading[p.id] ? "..." : "Mark as Received"}
                    </button>
                  )}
                  {!isCancelled && !isPaid && (
                    <button
                      onClick={() => cancelTx(p.id)}
                      className="px-4 py-2 border border-rosegold/30 text-champagne/70 text-[10px] tracking-[0.28em] uppercase rounded-sm hover:border-rosegold/60 transition-all flex items-center gap-2 justify-center"
                      data-testid={`cancel-payment-${p.id}`}
                    >
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
